<template>
  <transition :name="ns.b('fade')" @before-leave="onClose" @after-leave="$emit('destroy')">
    <div v-show="visible" :id="id" :class="[ns.b(), customClass, horizontalClass]" :style="positionStyle" role="alert"
      @mouseenter="clearTimer" @mouseleave="startTimer" @click="onClick">
      <z-icon v-if="iconComponent" :class="[ns.e('icon'), typeClass]">
        <component :is="iconComponent" />
      </z-icon>
      <div :class="ns.e('group')">
        <h2 :class="ns.e('title')" v-text="title" />
        <div v-show="message" :class="ns.e('content')" :style="!!title ? undefined : { margin: 0 }">
          <slot>
            <p v-if="!dangerouslyUseHTMLString">{{ message }}</p>
            <!-- Caution here, message could've been compromised, never use user's input as message -->
            <p v-else v-html="message" />
          </slot>
        </div>
        <z-icon v-if="showClose" :class="ns.e('closeBtn')" @click.stop="close">
          <component :is="closeIcon" />
        </z-icon>
      </div>
    </div>
  </transition>
</template>

<script lang="ts" setup>
import { computed, markRaw, onMounted, ref } from 'vue'
import { useEventListener, useTimeoutFn } from '@vueuse/core'
import { TypeComponentsMap, getEventCode } from '@zoryn/utils'
import { EVENT_CODE } from '@zoryn/constants'
import { ZIcon } from '@zoryn/components/icon'
import { useGlobalComponentSettings } from '@zoryn/components/config-provider'
import { notificationEmits } from './notification'
import { Close } from '@zoryn/icons'

import type { CSSProperties } from 'vue'
import type { NotificationProps } from './notification'

defineOptions({
  name: 'ZNotification',
})

const props = withDefaults(defineProps<NotificationProps>(), {
  customClass: '',
  duration: 4500,
  id: '',
  message: '',
  offset: 0,
  onClick: () => undefined,
  position: 'top-right',
  showClose: true,
  title: '',
  type: '',
  closeIcon: markRaw(Close),
})
defineEmits(notificationEmits)

const { ns, zIndex } = useGlobalComponentSettings('notification')
const { nextZIndex, currentZIndex } = zIndex

const visible = ref(false)
let timer: (() => void) | undefined = undefined

const typeClass = computed(() => {
  const type = props.type
  return type && TypeComponentsMap[props.type] ? ns.m(type) : ''
})

const iconComponent = computed(() => {
  if (!props.type) return props.icon
  return TypeComponentsMap[props.type] || props.icon
})

const horizontalClass = computed(() =>
  props.position.endsWith('right') ? 'right' : 'left'
)

const verticalProperty = computed(() =>
  props.position.startsWith('top') ? 'top' : 'bottom'
)

const positionStyle = computed<CSSProperties>(() => {
  return {
    [verticalProperty.value]: `${props.offset}px`,
    zIndex: props.zIndex ?? currentZIndex.value,
  }
})

function startTimer() {
  if (props.duration > 0) {
    ; ({ stop: timer } = useTimeoutFn(() => {
      if (visible.value) close()
    }, props.duration))
  }
}

function clearTimer() {
  timer?.()
}

function close() {
  visible.value = false
}

function onKeydown(event: KeyboardEvent) {
  const code = getEventCode(event)

  switch (code) {
    case EVENT_CODE.delete:
    case EVENT_CODE.backspace:
      clearTimer() // press delete/backspace clear timer
      break
    case EVENT_CODE.esc:
      // press esc to close the notification
      if (visible.value) {
        close()
      }
      break
    default: // resume timer
      startTimer()
      break
  }
}

// lifecycle
onMounted(() => {
  startTimer()
  nextZIndex()
  visible.value = true
})

useEventListener(document, 'keydown', onKeydown)

defineExpose({
  visible,
  /** @description close notification */
  close,
})
</script>
